// The canonical "<concentration><unit> in <volume> mL" string. It keys table
// rows, chart series and alignment lookups, so the regex lives here once.

import { normalizeToMcg } from './unitNormalization';

// The unit half is greedy up to " in " so compound units (units/mL, mEq/mL)
// survive intact.
const FORMULATION_RE = /^\s*([\d.]+)\s*([^\s]+(?:\/[^\s]+)?)\s+in\s+([\d.]+)\s*([a-zA-Z]+)?\s*$/;

// Strict variant: requires a "<alpha>/mL" numerator, for callers that will feed
// the parsed unit to getNumeratorUnit/UNIT_SCALE and want an unconvertible unit
// rejected up front rather than throwing deeper in the call stack.
const GRAM_STYLE_FORMULATION_RE = /^\s*([\d.]+)\s*([a-zA-Zµμ]+\/mL)\s+in\s+([\d.]+)\s*mL\s*$/;

/** Returns null when the string doesn't match, so callers can show it verbatim. */
export function parseFormulation(formulation) {
  if (!formulation || typeof formulation !== 'string') return null;

  const match = formulation.match(FORMULATION_RE);
  if (!match) return null;

  const concentration = Number(match[1]);
  const volume = Number(match[3]);
  if (!Number.isFinite(concentration) || !Number.isFinite(volume)) return null;

  return {
    concentration,
    concentrationUnit: match[2],
    volume,
    volumeUnit: match[4] || 'mL',
  };
}

/**
 * The same parse in the shape the store's row shaper and the scenario body use.
 * Kept distinct because unrelated layers consume the two shapes — renaming one
 * set of keys shouldn't silently change the other's payload.
 */
export function toStructuredFormulation(formulation) {
  const parsed = parseFormulation(formulation);
  if (!parsed) return null;

  return {
    concentration: parsed.concentration,
    unit: parsed.concentrationUnit,
    volume: parsed.volume,
  };
}

/**
 * Inverse of parseFormulation, so a round trip is stable — the string is a key.
 *
 * A bare numerator ("mcg") gains "/mL", because the picker supplies the unit
 * without its denominator and the built string has to match catalog entries.
 */
export function buildFormulationString(concentration, concentrationUnit, volume, volumeUnit = 'mL') {
  if (concentration == null || volume == null) return '';

  const unit = String(concentrationUnit ?? '');
  const qualifiedUnit = unit.includes('/') ? unit : `${unit}/mL`;

  return `${concentration} ${qualifiedUnit} in ${volume} ${volumeUnit}`;
}

/**
 * Parse only formulations whose concentration unit is "<alpha>/mL" and whose
 * volume is in mL. Screens the string's shape, not its dimension: "U/mL" is
 * alphabetic and parses fine, so activity units still reach the caller and are
 * rejected at conversion by normalizeUpdatedConcentration.
 */
export function parseGramStyleFormulation(formulation) {
  if (!formulation || typeof formulation !== 'string') return null;

  const match = formulation.match(GRAM_STYLE_FORMULATION_RE);
  if (!match) return null;

  const concentration = Number(match[1]);
  const volume = Number(match[3]);
  if (!Number.isFinite(concentration) || !Number.isFinite(volume)) return null;

  return { concentration, concentrationUnit: match[2], volume };
}

/**
 * Compare two formulations by NORMALIZED concentration + volume.
 *
 * String equality is wrong here: "0.1 mg/mL in 50 mL" and "100 mcg/mL in 50 mL"
 * are the same product, and the RTU catalog and utilization history routinely
 * express the same formulation in different units.
 */
export function formulationsMatch(a, b) {
  if (!a || !b) return false;

  const pa = parseFormulation(a);
  const pb = parseFormulation(b);
  if (!pa || !pb) return false;

  const concA = normalizeToMcg(pa.concentration, pa.concentrationUnit);
  const concB = normalizeToMcg(pb.concentration, pb.concentrationUnit);

  return Math.abs(concA - concB) < 0.01 && Math.abs(pa.volume - pb.volume) < 0.001;
}

/**
 * Case-insensitive exact match, falling back to the normalized comparison.
 * The restore path needs both: a saved formulation may come back byte-identical
 * or unit-shifted.
 */
export function formulationsEquivalent(a, b) {
  if (!a || !b) return false;
  if (String(a).toLowerCase() === String(b).toLowerCase()) return true;
  return formulationsMatch(a, b);
}

/** Concentration + unit only, for alignment badges that ignore volume. */
export function parseConcentration(formulation) {
  if (!formulation || typeof formulation !== 'string') return null;

  const match = formulation.match(/^\s*([\d.]+)\s*(\S+)/);
  if (!match) return null;

  const concentration = Number(match[1]);
  if (!Number.isFinite(concentration)) return null;

  return { concentration, unit: match[2] };
}

export default parseFormulation;
